import { Link } from "react-router-dom";
import { Camera, ThumbsUp, MapPin, Music2, Briefcase, ArrowRight } from "lucide-react";

const B = { fontFamily: "'Barlow', sans-serif" };

/* Einzige Quelle für die Kanalliste – Status spiegelt den echten Code-Stand:
   live = Posten funktioniert produktiv (Meta/Instagram/Facebook, Google-Profil).
   bald = angebunden, aber noch nicht freigegeben (TikTok: TikTok-Audit).
   Neutrale Icons + Wortmarke als Text – bewusst keine fremden Logos nachgebaut. */
export const KANAELE = [
  { key: "instagram", name: "Instagram", Icon: Camera, was: "Beiträge, Karussells und Reels in deinem Look.", to: null, status: "live", liveReady: true },
  { key: "facebook", name: "Facebook", Icon: ThumbsUp, was: "Beiträge und Reels – zeitgleich mit Instagram.", to: null, status: "live", liveReady: true },
  { key: "google", name: "Google-Profil", Icon: MapPin, was: "Google-Beiträge direkt in deinem Unternehmensprofil.", to: "/google", status: "live", liveReady: true },
  { key: "tiktok", name: "TikTok", Icon: Music2, was: "Kurze Hochkant-Videos aus deinem Clip – Robbie schneidet, textet und bereitet den Entwurf vor.", to: "/tiktok", status: "neu", liveReady: false },
  { key: "linkedin", name: "LinkedIn", Icon: Briefcase, was: "Beiträge für dein Geschäftsnetzwerk – persönliches Profil und Unternehmensseite.", to: null, status: "live", liveReady: false },
];

const Status = ({ status }) => {
  if (status === "live")
    return (
      <span style={B} className="inline-flex items-center gap-1.5 rounded-full bg-[#E7F3EC] px-2.5 py-1 text-[11px] font-bold text-[#1F5A3E]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#1F8A52]" aria-hidden="true" /> aktiv
      </span>
    );
  if (status === "neu")
    return (
      <span style={B} className="inline-flex items-center gap-1.5 rounded-full bg-[#DCF2F6] px-2.5 py-1 text-[11px] font-bold text-[#0A6E82]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#0FA7C2]" aria-hidden="true" /> neu
      </span>
    );
  return (
    <span style={B} className="inline-flex items-center gap-1.5 rounded-full bg-[#FBEFD6] px-2.5 py-1 text-[11px] font-bold text-[#8A5A00]">
      <span className="h-1.5 w-1.5 rounded-full bg-[#E0A100]" aria-hidden="true" /> in Kürze
    </span>
  );
};

/* Große Übersicht für die Social-Media-Landingpage. */
export const KanalUebersicht = ({ testId = "kanal-uebersicht" }) => (
  <section className="py-16 md:py-24 bg-[#FBFAF5]" data-testid={testId}>
    <div className="mx-auto max-w-[1300px] px-6 md:px-10">
      <p style={B} className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C574F]">Ein Chat, alle Kanäle</p>
      <h2 className="font-display font-extrabold uppercase leading-[0.95] text-3xl md:text-5xl text-[#111] mt-3">
        Alle Kanäle <span className="bg-[#FDD105] px-2">aus einer Hand.</span>
      </h2>
      <p style={B} className="mt-4 max-w-2xl text-base md:text-lg text-[#4A4A4A]">
        Du schickst Robbie ein Foto oder Video – er bereitet den passenden Beitrag für jeden Kanal vor. Du gibst einmal frei, der Rest läuft von selbst.
      </p>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`${testId}-grid`}>
        {KANAELE.map((k) => (
          <div key={k.key} className="flex flex-col rounded-3xl border border-black/10 bg-white p-6 transition-transform hover:-translate-y-0.5" data-testid={`kanal-${k.key}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#111] text-[#FDD105]">
                <k.Icon size={20} strokeWidth={2} aria-hidden="true" />
              </span>
              <Status status={k.status} />
            </div>
            <h3 className="font-display font-extrabold text-xl uppercase mt-4 text-[#111]">{k.name}</h3>
            <p style={B} className="mt-2 text-sm text-[#4A4A4A] flex-1">{k.was}</p>
            {k.to && (
              <Link to={k.to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#111] underline decoration-[#FDD105] decoration-2 underline-offset-4 hover:bg-[#FDD105]" data-testid={`kanal-${k.key}-link`}>
                Mehr erfahren <ArrowRight size={15} aria-hidden="true" />
              </Link>
            )}
          </div>
        ))}
      </div>
      <p style={B} className="mt-5 text-xs text-[#5C574F]" data-testid={`${testId}-hinweis`}>
        „aktiv“ heißt: Beiträge gehen nach deiner Freigabe produktiv raus. „in Kürze“ heißt: eingerichtet, aber noch in der offiziellen Freigabe des Netzwerks – wir schalten frei, sobald es so weit ist.
      </p>
    </div>
  </section>
);

/* Kompakte Chip-Liste – gleiche Kanäle für /preise und /portal#preise. */
export const KanalChips = ({ testId = "kanal-chips", titel = "Social-Media-Beiträge erscheinen auf diesen Kanälen" }) => (
  <div data-testid={testId}>
    <p style={B} className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C574F]">{titel}</p>
    <div className="mt-2.5 flex flex-wrap gap-2">
      {KANAELE.map((k) => (
        <span key={k.key} style={B} className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[13px] font-medium text-[#111]" data-testid={`kanal-chip-${k.key}`}>
          <k.Icon size={14} aria-hidden="true" /> {k.name}
          {k.status === "neu" && <span className="ml-0.5 rounded-full bg-[#DCF2F6] px-1.5 py-px text-[10px] font-bold not-italic text-[#0A6E82]">neu</span>}
          {k.status === "bald" && <span className="ml-0.5 rounded-full bg-[#FBEFD6] px-1.5 py-px text-[10px] font-bold not-italic text-[#8A5A00]">in Kürze</span>}
        </span>
      ))}
    </div>
  </div>
);
